/* eslint-disable max-len */
import { LeftOutlined } from '@ant-design/icons';
import { ApolloError } from '@apollo/client';
import { Col, Row, Typography } from 'antd';
import { Formik, useFormikContext } from 'formik';
import { get, isNil, isObject, keys } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { CustomerKind } from '../../../../api/types';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import Form from '../../../../components/fields/Form';
import FormAutoTouch, { toPath } from '../../../../components/FormAutoTouch';
import ScrollToTop from '../../../../components/ScrollToTop';
import BasicProLayoutContainer from '../../../../layouts/BasicProLayout/BasicProLayoutContainer';
import { useThemeComponents } from '../../../../themes';
import breakpoints from '../../../../utilities/breakpoints';
import { type UploadDocumentProp, getInitialValues } from '../../../../utilities/kycPresets';
import useKYCFormValidator from '../../../../utilities/kycPresets/useKYCValidators';
import useHandleError from '../../../../utilities/useHandleError';
import { usePersistentData } from '../../../../utilities/usePersistData';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import { getApplicantAgreements } from '../../../shared/CIPage/ConsentAndDeclarations/getAgreements';
import useAgreementSubmission from '../../../shared/CIPage/ConsentAndDeclarations/useAgreementSubmission';
import useAgreementsValidator from '../../../shared/CIPage/ConsentAndDeclarations/useAgreementsValidator';
import useAgreementsValues from '../../../shared/CIPage/ConsentAndDeclarations/useAgreementsValues';
import refineCustomerValues from '../../../shared/refineCustomerValues';
import useDeleteApplicationDocument from '../../../shared/useDeleteApplicationDocument';
import useRefineKycPresets from '../../../shared/useRefineKycPresets';
import useUploadApplicationDocument from '../../../shared/useUploadApplicationDocument';
import useCustomerDetailsSubmission from '../../StandardApplicationEntrypoint/CustomerDetailsPage/useCustomerDetailsSubmission';
import useUpdateApplicationFields from '../../StandardApplicationEntrypoint/CustomerDetailsPage/useUpdateApplicationFields';
import { getApplicantKyc } from '../../StandardApplicationEntrypoint/KYCPage/getKyc';
import { NextButton } from '../../StandardApplicationEntrypoint/shared/JourneyButton';
import { FooterButtonContainer, StyledBackButton } from '../../StandardApplicationEntrypoint/styledComponents';
import ConsentsAndDeclarations from '../ApplicantKycPage/ConsentsAndDeclarations';
import CustomerDetails from '../ApplicantKycPage/CustomerDetails';
import { type ApplicantKYCProps, FeatureKYCPageLabel, type MobilityKYCJourneyValues } from '../ApplicantKycPage/shared';
import VehicleInfo from '../Components/VehicleInfo';

const leftColSpan = { xl: 8, lg: 12, md: 24, xs: 24 };
const rightColSpan = { xl: 16, lg: 12, md: 24, xs: 24 };

const Title = styled.h4`
    padding-top: 12px;
    padding-bottom: 12px;
    font-size: 20px;
    font-weight: 900;
`;

const ApplicantKycContainer = styled.div`
    @media screen and (max-width: ${breakpoints.md}) {
        & .ant-pro-footer-bar {
            .ant-pro-footer-bar-right {
                width: 100%;

                .ant-btn {
                    width: 100%;
                }
            }
        }
    }
`;

export const mobilityGuarantorKYCKey = 'MobilityCuarantor';

const GuarantorKYC = ({ dispatch, state, endpoint, handleEndSession }: ApplicantKYCProps) => {
    const { token, application } = state;
    const { guarantorKYC, guarantorAgreements, guarantor, module } = application;
    const { t } = useTranslation('customerDetails');

    const { getItem } = usePersistentData();
    const { notification } = useThemeComponents();
    const [prefill, setPrefill] = useState<boolean>(false);

    const submitAgreements = useAgreementSubmission();
    const submitCustomerDetails = useCustomerDetailsSubmission();
    const updateApplicationFields = useUpdateApplicationFields();

    const isEditableField = useMemo(() => (guarantor ? guarantor[0].fields : []), [guarantor]);
    const agreementsKYC = guarantorAgreements.map(agreement => ({ ...agreement, isAgreed: false }));
    const applicantKycAgreements = useMemo(() => getApplicantAgreements(agreementsKYC), [agreementsKYC]);
    const agreementsValidator = useAgreementsValidator(applicantKycAgreements, 'agreements');
    const agreements = useAgreementsValues(applicantKycAgreements);

    const applicantKycFields = useMemo(() => getApplicantKyc(guarantorKYC), [guarantorKYC]);
    const kycPresets = useRefineKycPresets(applicantKycFields);

    const applicants = useMemo(() => {
        if (endpoint.mobilityApplicationModule.persistKYCData) {
            const sessionStorageValues =
                getItem<MobilityKYCJourneyValues>(mobilityGuarantorKYCKey)?.customer?.fields || {};
            if (get('UAEDrivingLicense.value[0].uploadDLCopy', sessionStorageValues)) {
                sessionStorageValues.UAEDrivingLicense.value[0].uploadDLCopy = null;
            }
            if (get('sessionStorageValues.UAEDrivingLicense.value[0].uploadEIDPassportCopy', sessionStorageValues)) {
                sessionStorageValues.UAEDrivingLicense.value[0].uploadEIDPassportCopy = null;
            }

            return { fields: { ...getInitialValues(isEditableField, kycPresets), ...sessionStorageValues } };
        }

        return { fields: { ...getInitialValues(isEditableField, kycPresets) } };
    }, [endpoint.mobilityApplicationModule.persistKYCData, getItem, isEditableField, kycPresets]);

    // Mobility booking does not have `proceed with customer`
    const applicantsValidator = useKYCFormValidator(
        kycPresets,
        module.__typename === 'MobilityModule' && module.company.countryCode,
        'customer.fields',
        false
    );
    const validations = useMemo(
        () => validators.compose(agreementsValidator, applicantsValidator),
        [agreementsValidator, applicantsValidator]
    );

    const validate = useValidator(validations, { prefill });

    const uploadDocument = useUploadApplicationDocument(token);
    const removeDocument = useDeleteApplicationDocument(token);

    const initialValues: MobilityKYCJourneyValues = useMemo(() => {
        if (isNil(getItem<MobilityKYCJourneyValues>(mobilityGuarantorKYCKey))) {
            return {
                agreements,
                customer: applicants,
                isCorporateCustomer: false,
                hasGuarantor: false,
                remarks: '',
                prefill: false,
            };
        }

        return { ...getItem<MobilityKYCJourneyValues>(mobilityGuarantorKYCKey), customer: applicants };
    }, [agreements, applicants, getItem]);

    const onSubmit = useHandleError(
        async (values: MobilityKYCJourneyValues & { remarks: string; prefill }) => {
            try {
                notification.loading({
                    content: t('customerDetails:messages.creationSubmitting'),
                    duration: 0,
                    key: 'primary',
                });

                const { applicantFields } = refineCustomerValues(values.customer.fields);

                const submitAgreementKYC = await submitAgreements(
                    state.token,
                    values.agreements,
                    CustomerKind.Guarantor,
                    false
                );

                const submitApplicantKYC = // Mobility booking does not have `proceed with customer`
                    await submitCustomerDetails(
                        submitAgreementKYC.token,
                        applicantFields,
                        CustomerKind.Guarantor,
                        values.prefill,
                        false
                    );

                notification.destroy('primary');

                if (submitApplicantKYC.__typename !== 'ApplicationJourney') {
                    throw new Error('unsupported journey context');
                }

                // go to the journey
                if (submitApplicantKYC.application.__typename !== 'MobilityApplication') {
                    throw new Error('unexpected type');
                }

                dispatch({
                    type: 'next',
                    token: submitApplicantKYC.token,
                    application: submitApplicantKYC.application,
                });
            } catch (error) {
                if (error instanceof ApolloError) {
                    notification.error(error.graphQLErrors[0].message);
                }
            } finally {
                notification.destroy('primary');
            }
        },
        [notification, t, submitAgreements, state.token, submitCustomerDetails, updateApplicationFields, dispatch],
        {}
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            <Inner
                dispatch={dispatch}
                endpoint={endpoint}
                handleEndSession={handleEndSession}
                mobilityKycPreset={kycPresets}
                removeDocument={removeDocument}
                setPrefill={setPrefill}
                showTabs={false}
                state={state}
                uploadDocument={uploadDocument}
            />
        </Formik>
    );
};

const Inner = ({
    state,
    handleEndSession,
    mobilityKycPreset: kycPresets,
    showTabs,
    setPrefill,
    endpoint,
    uploadDocument,
    removeDocument,
}: ApplicantKYCProps & UploadDocumentProp) => {
    const { application } = state;
    const { applicantAgreements } = application;
    const { t } = useTranslation('customerDetails');
    const { isSubmitting, handleSubmit, validateForm, submitForm, values, errors, setFieldTouched } =
        useFormikContext<MobilityKYCJourneyValues>();
    const navigate = useNavigate();
    const { MobilityWebpageLayout } = useThemeComponents();

    const { setItem, getItem } = usePersistentData();
    const { sessionTimeout } = useCompany();

    const latestValues = useRef(values);
    const firstValidation = useRef(!isNil(getItem(mobilityGuarantorKYCKey)));
    latestValues.current = values;

    useEffect(() => {
        const saveData = () => {
            if (endpoint.mobilityApplicationModule.persistKYCData) {
                setItem(mobilityGuarantorKYCKey, latestValues.current, sessionTimeout * 60, null);
            }
        };

        window.addEventListener('beforeunload', saveData);

        return () => {
            saveData();
            window.removeEventListener('beforeunload', saveData);
        };
    }, [endpoint.mobilityApplicationModule.persistKYCData, sessionTimeout, setItem, latestValues]);

    useEffect(() => {
        const touchOnErrors = (fields, prefix: (string | number)[] = []) => {
            for (const key of keys(fields)) {
                const item = fields[key];

                if (isObject(item) || Array.isArray(item)) {
                    touchOnErrors(item, [...prefix, key]);
                }

                if (typeof item === 'string' || typeof item === 'number') {
                    const path = toPath([...prefix, key]);
                    setFieldTouched(path, true, false);
                }
            }
        };
        if (firstValidation.current) {
            touchOnErrors(errors);
            firstValidation.current = true;
        }
    }, [errors, setFieldTouched]);

    const onSubmit = useCallback(async () => {
        await validateForm();
        await submitForm();
    }, [submitForm, validateForm]);

    const agreementsKYC = applicantAgreements.map(agreement => ({ ...agreement, isAgreed: false }));
    const applicantKycAgreements = useMemo(() => getApplicantAgreements(agreementsKYC), [agreementsKYC]);

    const title = (
        <Row style={{ width: '100%' }}>
            <Typography>{t('customerDetails:title')}</Typography>
        </Row>
    );

    const onBack = useCallback(() => {
        navigate(-1);

        if (handleEndSession) {
            handleEndSession();
        }
    }, [handleEndSession, navigate]);

    return (
        <ApplicantKycContainer>
            <ScrollToTop />
            <FormAutoTouch />
            <MobilityWebpageLayout
                backIcon={
                    <StyledBackButton>
                        <LeftOutlined /> {t('customerDetails:backButton')}
                    </StyledBackButton>
                }
                footer={[
                    <FooterButtonContainer>
                        <NextButton key="nextButton" disabled={isSubmitting} onSubmit={onSubmit} />
                    </FooterButtonContainer>,
                ]}
                onBack={onBack}
                preferredMobileFooterHeight={75}
                title={title}
                hasFooterBar
            >
                <BasicProLayoutContainer>
                    <Form id="kycJourneyForm" name="kycJourneyForm" onSubmitCapture={handleSubmit}>
                        <Row gutter={[24, 50]}>
                            <Col {...leftColSpan}>
                                <Title>{t('customerDetails:sectionTitles.mobilitySelectedCar')}</Title>
                                <VehicleInfo application={application} />
                            </Col>
                            <Col {...rightColSpan}>
                                <Row gutter={[16, 16]}>
                                    <Col span={24}>
                                        <CustomerDetails
                                            customerKind={CustomerKind.Guarantor}
                                            hasGuarantorPreset={false}
                                            kycPresets={kycPresets}
                                            removeDocument={removeDocument}
                                            setPrefill={setPrefill}
                                            showTabs={showTabs}
                                            typeKYCPageTitle={FeatureKYCPageLabel.Default}
                                            uploadDocument={uploadDocument}
                                        />
                                    </Col>
                                    <Col span={24}>
                                        <ConsentsAndDeclarations applicationAgreements={applicantKycAgreements} />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Form>
                </BasicProLayoutContainer>
            </MobilityWebpageLayout>
        </ApplicantKycContainer>
    );
};

export default GuarantorKYC;
